"use client";

import { useState } from "react";
import { Pencil, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { goalDisplay, formatGoalProgress } from "@/lib/utils/goalDisplay";
import type { FamilyCountGoal, FamilyOfflineCountGoal, Member } from "@/lib/schemas";

type Props = {
  goal: FamilyCountGoal | FamilyOfflineCountGoal;
  /** Aktualny progres — liczba zadań od `goal.createdAt` (z useGoalProgress). */
  progress: number;
  /** Mapa uid → liczba zadań tego usera (z useGoalProgress). */
  breakdown: Map<string, number>;
  /** Members do nazewnictwa breakdown'u. */
  members: readonly Member[];
  /** Klik w "Zarządzaj" — otwiera GoalDialog w trybie edycji. */
  onEdit?: () => void;
  /** Renderuj jako zwinięty accordion (slim pasek + tap rozwija). Default true. */
  collapsible?: boolean;
  /**
   * Cel został właśnie osiągnięty (w oknie celebracji — zwykle 5 dni).
   * Zmienia banderolę u góry na "🎉 Cel osiągnięty!" i pokazuje pełen
   * pasek bez tekstu "zostało X". Reszta layoutu (contributors, gradient,
   * onEdit) bez zmian.
   */
  isCelebrating?: boolean;
};

/**
 * Karta celu rodziny — gradient w kolorze meta (amber dla family_count,
 * brąz dla family_offline_count). Pod paskiem postępu — breakdown kto ile
 * dorzucił (transparentność rodzinna, nie konkurs).
 *
 * Source of truth progresu: liczone z `useGoalProgress` (approved completion
 * od `createdAt`), bez cache w doc — `currentMinor` znika.
 */
export function FamilyGoalCard({
  goal,
  progress,
  breakdown,
  members,
  onEdit,
  collapsible = true,
  isCelebrating = false,
}: Props) {
  const [open, setOpen] = useState(!collapsible || isCelebrating);
  const meta = goalDisplay(goal);
  const fmt = formatGoalProgress(goal, progress);
  const target = goal.targetCount;
  // W trybie celebracji pasek zawsze 100% — progress może być >= target
  // (jeśli więcej zadań po osiągnięciu) albo dokładnie =target.
  const pct = isCelebrating
    ? 100
    : target > 0 ? Math.min(100, Math.round((progress / target) * 100)) : 0;
  const remaining = Math.max(0, target - progress);
  const emoji = goal.emoji ?? meta.emoji;
  const memberByUid = new Map(members.map((m) => [m.userId, m]));
  const topLabel = isCelebrating ? "🎉 Cel osiągnięty!" : `⭐ ${meta.shortLabel}`;

  // Breakdown — pokazujemy tylko tych z >0 wkładem, sort desc po count.
  const contributors = Array.from(breakdown.entries())
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([uid, n]) => ({
      uid,
      name: memberByUid.get(uid)?.displayName?.split(" ")[0] ?? "—",
      count: n,
    }));

  if (collapsible) {
    return (
      <div
        className="relative overflow-hidden rounded-[18px] text-white"
        style={{
          background: `linear-gradient(135deg, ${meta.gradientFrom}, ${meta.gradientTo})`,
          boxShadow: meta.shadow,
        }}
      >
        {open && (
          <div
            aria-hidden
            className="absolute pointer-events-none"
            style={{
              right: -10,
              bottom: -16,
              fontSize: 110,
              opacity: 0.18,
              lineHeight: 1,
            }}
          >
            {emoji}
          </div>
        )}

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="relative w-full text-left px-4 py-2.5"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.05em] opacity-90 truncate">
                {topLabel}
              </span>
              <span className="text-[11px] font-num font-semibold opacity-95 whitespace-nowrap">
                {fmt.current}/{fmt.target}
              </span>
            </div>
            <div
              className="font-display text-[14px] font-semibold mt-0.5 flex items-center gap-1.5 min-w-0"
              style={{ letterSpacing: "-0.025em" }}
            >
              <span className="truncate">{goal.title}</span>
              <ChevronDown
                className={cn(
                  "w-3.5 h-3.5 transition-transform shrink-0 opacity-80",
                  open && "rotate-180",
                )}
              />
            </div>
            <div className="h-1.5 rounded-full bg-white/25 mt-1.5 overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-[width] duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </button>

        {open && (
          <div className="relative px-4 pb-4 pt-1">
            <div className="text-[12px] opacity-90">
              {isCelebrating
                ? "Razem daliśmy radę. Dziękujemy każdemu!"
                : remaining === 0
                  ? "Cel osiągnięty 🎉"
                  : `Zostało ${remaining} ${meta.unitLabelGen}`}
            </div>

            {contributors.length > 0 && (
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {contributors.map((c) => (
                  <span
                    key={c.uid}
                    className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold"
                  >
                    <span>{c.name}</span>
                    <span className="font-num opacity-90">·</span>
                    <span className="font-num">{c.count}</span>
                  </span>
                ))}
              </div>
            )}

            {onEdit ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="absolute top-0 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"
                aria-label="Zarządzaj celem"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden rounded-[22px] p-5 text-white"
      style={{
        background: `linear-gradient(135deg, ${meta.gradientFrom}, ${meta.gradientTo})`,
        boxShadow: meta.shadow,
      }}
    >
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          right: -10,
          bottom: -16,
          fontSize: 110,
          opacity: 0.18,
          lineHeight: 1,
        }}
      >
        {emoji}
      </div>

      <div className="relative">
        <div className="text-[11px] font-bold uppercase tracking-[0.05em] opacity-90">
          {topLabel}
        </div>

        <div
          className="font-display text-[22px] font-semibold mt-1"
          style={{ letterSpacing: "-0.025em" }}
        >
          {goal.title}
        </div>

        <div className="text-[14px] font-semibold mt-2 opacity-95">
          <span className="font-display text-[22px] mr-1">{fmt.current}</span>
          z {fmt.target} {meta.unitLabel}
        </div>

        <div className="h-2.5 rounded-full bg-white/25 mt-2 overflow-hidden">
          <div
            className="h-full bg-white rounded-full transition-[width] duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>

        <div className="text-[12px] mt-2 opacity-90">
          {isCelebrating
            ? "Razem daliśmy radę. Dziękujemy każdemu!"
            : remaining === 0
              ? "Cel osiągnięty 🎉"
              : `Zostało ${remaining} ${meta.unitLabelGen}`}
        </div>

        {contributors.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {contributors.map((c) => (
              <span
                key={c.uid}
                className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold"
              >
                <span>{c.name}</span>
                <span className="font-num opacity-90">·</span>
                <span className="font-num">{c.count}</span>
              </span>
            ))}
          </div>
        )}

        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="absolute top-0 right-0 -mt-1 -mr-1 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"
            aria-label="Zarządzaj celem"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
